import type { JSX } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import type { Experience, ExperienceStatus } from '../types/experience.types';
import { EXPERIENCE_STATUS_TRANSITIONS } from '../types/experience.types';

interface ExperienceActionsProps {
  experience: Experience;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: ExperienceStatus) => void;
}

const statusLabels: Record<ExperienceStatus, string> = {
  DRAFT: 'Draft',
  AWAITING: 'Awaiting',
  CONFIRMED: 'Confirmed',
  REJECTED: 'Rejected',
};

export function ExperienceActions({
  experience,
  onEdit,
  onDelete,
  onStatusChange,
}: ExperienceActionsProps): JSX.Element {
  const availableTransitions = EXPERIENCE_STATUS_TRANSITIONS[experience.status];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />
          Edit
        </DropdownMenuItem>
        {availableTransitions.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {availableTransitions.map((status) => (
              <DropdownMenuItem key={status} onClick={() => onStatusChange(status)}>
                Change to {statusLabels[status]}
              </DropdownMenuItem>
            ))}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDelete} className="text-destructive">
          <Trash2 className="mr-2 h-4 w-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
